/**
 * The drone feed (WORKBOARD I9; decision 88: "plain, not greebly"): a camera that rides one of your
 * team's drones, dressed as a live transmission (ui/feedFrame.ts — the frame it shares with the
 * missile cam): a few readouts (battery, altitude, speed, bearing) and each contact the drone makes
 * boxed as it is made, fading as the sighting does. When the drone's flight ends (route done, battery
 * out, shot down) the feed says so for a moment and closes.
 *
 * V in the real-time game (a called-out hook in lane C's game/actionHost.ts): your latest drone in the
 * air, then the team's next, then off. While the feed is up your soldier stands still — you are looking
 * through the drone, not around you (the host idles its input, as for the tablet).
 *
 * The camera sits at the drone and looks down along its route, ahead of it; the maths is pure and
 * exported (tests/droneFeed.test.ts). Presentation only: reads state, writes none.
 */
import * as THREE from 'three';
import type { DroneState, GameState, Vec3 } from '../types';
import { dronePos, droneGroundDir } from '../core/drones';
import { getSurfaceRadius } from '../core/surface';
import { personSetOf, PERSON_BODY_HEIGHT } from '../core/scaleProfile';
import { alongRoute } from './topoMap';
import { confidenceOf } from '../rendering/layer0Views';
import { FeedFrame } from './feedFrame';

/** The feed's field of view (degrees) and how far ahead of the drone it looks (in its altitudes). */
export const FEED_FOV = 55;
export const FEED_LOOK_AHEAD = 3;
/** How long "signal lost" stays up when a drone's flight ends (seconds). */
export const FEED_LOST_SECONDS = 1.4;

const norm = (v: Vec3): Vec3 => { const l = Math.hypot(v.x, v.y, v.z) || 1; return { x: v.x / l, y: v.y / l, z: v.z / l }; };
const dot = (a: Vec3, b: Vec3) => a.x * b.x + a.y * b.y + a.z * b.z;

/** The drones a viewer's feed can show, latest first: the viewer's own, then the rest of the team's. */
export function feedCandidates(state: Pick<GameState, 'drones'>, team: number, operatorId: number | null): DroneState[] {
  const live = (state.drones ?? []).filter(d => d.alive && d.teamIndex === team);
  const mine = live.filter(d => d.operatorId === operatorId).sort((a, b) => b.id - a.id);
  const rest = live.filter(d => d.operatorId !== operatorId).sort((a, b) => b.id - a.id);
  return [...mine, ...rest];
}

/** What the feed reads out: battery seconds left, altitude and speed in people, the bearing it flies (degrees from north). */
export function feedReadouts(drone: DroneState, state: Pick<GameState, 'planets' | 'config'>): { battery: number; batteryShare: number; altitude: number; speed: number; bearing: number } {
  const ps = personSetOf(state), person = PERSON_BODY_HEIGHT * ps.k;
  const planet = state.planets[drone.planetIndex];
  const here = droneGroundDir(drone, planet.radius);
  const ahead = alongRoute(drone.route, planet.radius, drone.flown + 0.01 * planet.radius);
  // north: toward the planet's +Y (or +Z near the poles), as the topo map draws it
  const ref = Math.abs(here.y) < 0.95 ? { x: 0, y: 1, z: 0 } : { x: 0, y: 0, z: 1 };
  const north = norm({ x: ref.x - here.x * dot(ref, here), y: ref.y - here.y * dot(ref, here), z: ref.z - here.z * dot(ref, here) });
  const east = norm({ x: north.y * here.z - north.z * here.y, y: north.z * here.x - north.x * here.z, z: north.x * here.y - north.y * here.x });
  const d = { x: ahead.x - here.x, y: ahead.y - here.y, z: ahead.z - here.z };
  const bearing = ((Math.atan2(dot(d, east), dot(d, north)) * 180) / Math.PI + 360) % 360;
  const battery = Math.max(0, ps.droneEndurance - drone.age);
  return { battery, batteryShare: battery / ps.droneEndurance, altitude: ps.droneAltitude / person, speed: ps.droneSpeed / person, bearing };
}

/**
 * Where the feed's camera is and what it looks at: at the drone, down toward the ground FEED_LOOK_AHEAD
 * altitudes ahead along its route (past the route's end, at its end). World coordinates.
 */
export function feedView(drone: DroneState, state: Pick<GameState, 'planets' | 'config'>): { eye: Vec3; target: Vec3; up: Vec3 } {
  const planet = state.planets[drone.planetIndex];
  const eye = dronePos(drone, state);
  const ps = personSetOf(state);
  const aheadDir = alongRoute(drone.route, planet.radius, drone.flown + FEED_LOOK_AHEAD * ps.droneAltitude);
  const r = getSurfaceRadius(planet, aheadDir);
  const target = { x: planet.position.x + aheadDir.x * r, y: planet.position.y + aheadDir.y * r, z: planet.position.z + aheadDir.z * r };
  const up = norm({ x: eye.x - planet.position.x, y: eye.y - planet.position.y, z: eye.z - planet.position.z });
  return { eye, target, up };
}

export class DroneFeed {
  private readonly frame: FeedFrame;
  private droneId: number | null = null;
  private lostAt: number | null = null;
  private time = 0;
  private readonly onKey: (e: KeyboardEvent) => void;
  private listening = false;

  /**
   * `hooks.cycle`: V while the feed is up (the host is idled then, so the feed takes its own keys);
   * `hooks.closed`: the feed has closed (give the host its input back).
   */
  constructor(canvas: HTMLCanvasElement, private readonly hooks: { cycle: () => void; closed: () => void }) {
    this.frame = new FeedFrame(canvas, 'drone-feed');
    this.onKey = (e: KeyboardEvent) => {
      if (e.key === '`') return;
      const k = e.key.toLowerCase();
      if (k === 'v' && !e.repeat) this.hooks.cycle();
      else if (k === 'escape') this.close();
      e.preventDefault(); e.stopImmediatePropagation();
    };
  }

  get active(): boolean { return this.droneId !== null; }
  get showing(): number | null { return this.droneId; }

  /**
   * V: from off, the first candidate; on, the next one after the drone shown; past the last, off.
   * Returns whether the feed is up.
   */
  cycle(state: Pick<GameState, 'drones'>, team: number, operatorId: number | null): boolean {
    const list = feedCandidates(state, team, operatorId);
    if (this.droneId === null) { if (list.length) this.open(list[0].id); }
    else {
      const i = list.findIndex(d => d.id === this.droneId);
      if (i >= 0 && i + 1 < list.length) this.open(list[i + 1].id);
      else this.close();
    }
    return this.active;
  }

  private open(id: number): void {
    this.droneId = id; this.lostAt = null;
    this.frame.show();
    if (!this.listening) { window.addEventListener('keydown', this.onKey, true); this.listening = true; }
  }

  close(): void {
    const was = this.droneId !== null;
    this.droneId = null; this.lostAt = null;
    this.frame.hide();
    if (this.listening) { window.removeEventListener('keydown', this.onKey, true); this.listening = false; }
    if (was) this.hooks.closed();
  }

  /**
   * Place the camera on the drone and draw the overlay. Returns false when the feed has closed
   * (its drone ended and "signal lost" has run its moment) so the host can take its camera back.
   */
  update(state: GameState, camera: THREE.PerspectiveCamera, tick: number, dt: number): boolean {
    if (this.droneId === null) return false;
    this.time += dt;
    const drone = (state.drones ?? []).find(d => d.id === this.droneId);
    if (!drone) { this.close(); return false; }
    if (!drone.alive) {
      if (this.lostAt === null) { this.lostAt = this.time; this.frame.message(drone.end === 'shotDown' ? 'SIGNAL LOST — SHOT DOWN' : drone.end === 'endurance' ? 'SIGNAL LOST — BATTERY' : 'FEED ENDED — ROUTE FLOWN'); }
      if (this.time - this.lostAt > FEED_LOST_SECONDS) { this.close(); return false; }
    }
    // the camera rides the drone (frozen where it ended while "signal lost" shows)
    const v = feedView(drone, state);
    camera.position.set(v.eye.x, v.eye.y, v.eye.z);
    camera.up.set(v.up.x, v.up.y, v.up.z);
    camera.lookAt(v.target.x, v.target.y, v.target.z);
    if (camera.fov !== FEED_FOV) { camera.fov = FEED_FOV; camera.updateProjectionMatrix(); }
    camera.updateMatrixWorld(true);   // the soldier's camera updated its matrices earlier this frame: project contacts through the drone's
    // readouts
    const r = feedReadouts(drone, state);
    this.frame.readout('tl', `<span class="live"></span>DRONE ${drone.id} · ${drone.alive ? 'LIVE' : 'NO SIGNAL'}`);
    this.frame.readout('tr', `BAT ${Math.ceil(r.battery)} S<span class="bar"><i style="width:${Math.round(r.batteryShare * 100)}%"></i></span>`);
    this.frame.readout('bl', `ALT ${Math.round(r.altitude)} · SPD ${Math.round(r.speed)} PEOPLE/S`);
    this.frame.readout('br', `BRG ${String(Math.round(r.bearing) % 360).padStart(3, '0')}°`);
    // contacts this drone has made, boxed where they were seen, fading as the sighting does
    this.frame.beginMarks();
    const person = PERSON_BODY_HEIGHT * personSetOf(state).k;
    for (const s of state.sightings ?? []) {
      if (s.by !== 'drone' || s.sourceId !== drone.id) continue;
      const c = confidenceOf(s, tick);
      if (c <= 0) continue;
      this.frame.box(camera, s.pos, 2.2 * person, 'CONTACT', 0.35 + 0.65 * c);
    }
    return true;
  }

  dispose(): void { this.close(); this.frame.dispose(); }
}
